// src/features/settings/AppearanceSettings.tsx
import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Select, MenuItem, SelectChangeEvent, Button, Alert, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import { setThemeMode } from '../../app/slices/uiSlice';
import { updateUserPreferences, clearAuthStatus } from '../../features/auth/authSlice';
import { User } from '../../types';
import { languages } from '../../config/languageConfig';
import i18n from "../../i18n"; // Import centralized config

interface AppearanceSettingsProps {
    user: User;
}

const AppearanceSettings: React.FC<AppearanceSettingsProps> = ({ user }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { status, message, error } = useAppSelector(state => state.auth);
    const [selectedTheme, setSelectedTheme] = useState(user.preferences.theme || 'light');
    const [selectedLanguage, setSelectedLanguage] = useState(user.preferences.language || 'en_US');
    const [isThemeChanged, setIsThemeChanged] = useState(false);
    const [isLanguageChanged, setIsLanguageChanged] = useState(false);

    // Clear auth status on component mount
    useEffect(() => {
        dispatch(clearAuthStatus());
    }, [dispatch]);

    // Reset change tracking after successful save
    useEffect(() => {
        if (status.user === 'succeeded' && message) {
            setIsThemeChanged(false);
            setIsLanguageChanged(false);
        }
    }, [status.user, message]);

    // Handle theme change (immediate UI update, not saved yet)
    const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const mode = event.target.value as 'light' | 'dark';
        setSelectedTheme(mode);
        dispatch(setThemeMode(mode));
        setIsThemeChanged(mode !== user.preferences.theme);
    };

    // Handle language change (immediate UI update, not saved yet)
    const handleLanguageChange = (event: SelectChangeEvent<string>) => {
        const lang = event.target.value;
        setSelectedLanguage(lang);
        i18n.changeLanguage(lang);
        setIsLanguageChanged(lang !== user.preferences.language);
    };

    // Handle save preferences
    const handleSavePreferences = () => {
        dispatch(updateUserPreferences({
            theme: selectedTheme,
            language: selectedLanguage,
        }));
    };

    // Determine if Save button should be disabled
    const isButtonDisabled = status.user === 'loading' || (!isThemeChanged && !isLanguageChanged);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>{t('menu_appearance')}</Typography>
            {/* Display success or error alerts below the main title */}
            {message && <Alert severity="success" sx={{ mb: 2 }}>{t('appearance_save_success')}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Theme Section */}
            <Box sx={{ py: 3 }}>
                <Typography variant="h6" gutterBottom>{t('appearance_theme')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('appearance_theme_desc')}
                </Typography>
                <FormControl sx={{ mb: 3 }}>
                    <RadioGroup row value={selectedTheme} onChange={handleThemeChange}>
                        <FormControlLabel value="light" control={<Radio />} label={t('appearance_light')} />
                        <FormControlLabel value="dark" control={<Radio />} label={t('appearance_dark')} />
                    </RadioGroup>
                </FormControl>
            </Box>

            {/* Language Section */}
            <Box sx={{ py: 3 }}>
                <Typography variant="h6" gutterBottom>{t('language_switcher_title')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('language_switcher_desc')}
                </Typography>
                <FormControl fullWidth>
                    <Select value={selectedLanguage} onChange={handleLanguageChange} sx={{ maxWidth: 200, mt: 1 }}>
                        {languages.map(lang => (
                            <MenuItem key={lang.code} value={lang.code}>{lang.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Save Button Section */}
            <Box sx={{ py: 3 }}>
                <Button
                    variant="contained"
                    disabled={isButtonDisabled}
                    onClick={handleSavePreferences}
                    sx={{
                        bgcolor: 'inverse.main',
                        color: 'inverse.contrastText',
                        '&:hover': {
                            bgcolor: 'inverse.hover.main',
                        },
                    }}
                >
                    {status.user === 'loading' ? <CircularProgress size={24} color="inherit" /> : t('save_changes')}
                </Button>
            </Box>
        </Box>
    );
};

export default AppearanceSettings;